import React, { useEffect, useRef, useState } from 'react'
import * as Blockly from 'blockly'
import { javascriptGenerator } from 'blockly/javascript'

const TOOLBOX = {
  kind: 'categoryToolbox',
  contents: [
    { kind: 'category', name: 'Missão BNCC', colour: '#1d4ed8', contents: [
      { kind: 'block', type: 'bncc_problem' },
      { kind: 'block', type: 'bncc_answer' },
      { kind: 'block', type: 'bncc_check' }
    ] },
    { kind: 'category', name: 'Lógica', colour: '#2563eb', contents: [{ kind: 'block', type: 'controls_if' }, { kind: 'block', type: 'logic_compare' }, { kind: 'block', type: 'logic_boolean' }] },
    { kind: 'category', name: 'Laços', colour: '#0f766e', contents: [{ kind: 'block', type: 'controls_repeat_ext' }, { kind: 'block', type: 'math_number' }] },
    { kind: 'category', name: 'Matemática', colour: '#9333ea', contents: [{ kind: 'block', type: 'math_arithmetic' }, { kind: 'block', type: 'math_number' }, { kind: 'block', type: 'math_round' }] },
    { kind: 'category', name: 'Texto', colour: '#ea580c', contents: [{ kind: 'block', type: 'text' }, { kind: 'block', type: 'text_print' }] },
    { kind: 'category', name: 'Variáveis', custom: 'VARIABLE', colour: '#db2777' }
  ]
}

function ensureCustomBlocks() {
  if (!Blockly.Blocks.bncc_problem) {
    Blockly.Blocks.bncc_problem = {
      init() {
        this.appendDummyInput().appendField('problema BNCC').appendField(new Blockly.FieldTextInput('Resolva o problema'), 'PROMPT')
        this.setNextStatement(true)
        this.setColour('#1d4ed8')
        this.setTooltip('Contexto matemático da atividade selecionada.')
      }
    }
  }
  if (!Blockly.Blocks.bncc_answer) {
    Blockly.Blocks.bncc_answer = {
      init() {
        this.appendValueInput('VALUE').setCheck('Number').appendField('resposta calculada')
        this.setPreviousStatement(true)
        this.setNextStatement(true)
        this.setColour('#7c3aed')
      }
    }
  }
  if (!Blockly.Blocks.bncc_check) {
    Blockly.Blocks.bncc_check = {
      init() {
        this.appendDummyInput().appendField('mostrar e conferir resposta')
        this.setPreviousStatement(true)
        this.setColour('#059669')
      }
    }
  }

  javascriptGenerator.forBlock.bncc_problem = (block) => {
    const prompt = block.getFieldValue('PROMPT') || ''
    return `console.log(${JSON.stringify(prompt)});\n`
  }
  javascriptGenerator.forBlock.bncc_answer = (block, generator) => {
    const value = generator.valueToCode(block, 'VALUE', generator.ORDER_NONE) || '0'
    return `const resposta = ${value};\n`
  }
  javascriptGenerator.forBlock.bncc_check = () => 'console.log("Resposta calculada:", typeof resposta !== "undefined" ? resposta : "monte o cálculo");\n'
}

function starterState(context) {
  const prompt = `${context.grade}º ano • ${context.topic} • ${context.skillReference || 'BNCC'} • ${context.profile || 'perfil geral'} — ${context.prompt || 'Monte o algoritmo matemático.'}`
  return {
    blocks: {
      languageVersion: 0,
      blocks: [{
        type: 'bncc_problem',
        id: 'bncc-problem',
        x: 30,
        y: 30,
        fields: { PROMPT: prompt },
        next: {
          block: {
            type: 'bncc_answer',
            id: 'bncc-answer',
            inputs: { VALUE: { block: { type: 'math_number', fields: { NUM: Number(context.seedAnswer || 0) } } } },
            next: { block: { type: 'bncc_check', id: 'bncc-check' } }
          }
        }
      }]
    }
  }
}

export default function BlocklyWorkspace({ value, onChange, context = {} }) {
  const divRef = useRef(null)
  const workspaceRef = useRef(null)
  const [error, setError] = useState('')
  const contextKey = [context.grade, context.topic, context.skillReference, context.profile, context.exerciseId, context.prompt].join('|')

  useEffect(() => {
    if (!divRef.current) return undefined
    ensureCustomBlocks()

    try {
      const workspace = Blockly.inject(divRef.current, {
        toolbox: TOOLBOX,
        media: '/vendor/blockly/media/',
        trashcan: true,
        scrollbars: true,
        zoom: { controls: true, wheel: true, startScale: 0.9, maxScale: 1.4, minScale: 0.5 }
      })
      workspaceRef.current = workspace
      Blockly.serialization.workspaces.load(starterState(context), workspace)

      const sync = () => {
        try {
          const code = javascriptGenerator.workspaceToCode(workspace)
          onChange?.(code)
        } catch (err) {
          setError(`Falha ao gerar o código dos blocos: ${err?.message || 'erro desconhecido'}`)
        }
      }

      workspace.addChangeListener(sync)
      sync()
      setTimeout(() => Blockly.svgResize(workspace), 0)
    } catch (err) {
      setError(`Não foi possível inicializar o Blockly localmente. ${err?.message || ''}`.trim())
    }

    return () => {
      if (workspaceRef.current) workspaceRef.current.dispose()
      workspaceRef.current = null
    }
  }, [contextKey])

  return (
    <div className="stack-sm">
      <div className="surface-card compact">
        <strong>{context.grade}º ano • {context.topic}</strong>
        <p>{context.skillReference ? `${context.skillReference} • ` : ''}{context.profile ? `Perfil: ${context.profile}` : 'Perfil geral'}.</p>
        {context.prompt ? <p>{context.prompt}</p> : null}
      </div>
      {error ? <div className="alert">{error}</div> : null}
      <div ref={divRef} style={{ minHeight: 360, width: '100%', background: '#fff', borderRadius: 16, border: '1px solid var(--line)' }} />
      {value ? <small className="muted">Código sincronizado com os blocos.</small> : null}
    </div>
  )
}
